import { Checkbox, Drawer, Modal } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createRoleAPI,
  editRoleAPI,
  getAllRoleModulesAPI,
  deleteRoleAPI,
} from "../../../Redux/Apis/roleApi";
import Loader from "../../atoms/Loader/Loader";
import Input from "../../atoms/Input/Input";
import AddButton from "../../atoms/AddButton/AddButton";
import RoleSelection from "../../atoms/RoleSelection";
import EmployeeSelect from "../../atoms/EmployeeSelect/EmployeeSelect";
import { validateEmail, permissionType } from "../../../app/utils/common/Functions";

const formatAttendancePermissionLabel = (permission) => {
  const legacy = {
    read: "Legacy · read",
    "read users": "Legacy · read users",
    update: "Legacy · update",
  };
  if (legacy[permission]) return legacy[permission];
  const m = String(permission).match(/^(overview|history|team|integration)_(read|write|update)$/);
  if (m) {
    const section = m[1].charAt(0).toUpperCase() + m[1].slice(1);
    const action = m[2].charAt(0).toUpperCase() + m[2].slice(1);
    return `${section} · ${action}`;
  }
  return permission;
};

/** API / MySQL may return JSON array or string; normalize for checkbox list. */
const normalizeModuleKeys = (keys) => {
  if (Array.isArray(keys)) return keys.map(String);
  if (keys == null) return [];
  if (typeof keys === "object") return Object.values(keys).map(String);
  if (typeof keys === "string") {
    try {
      const p = JSON.parse(keys);
      return Array.isArray(p) ? p.map(String) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const ATTENDANCE_GROUPS = [
  { title: "Legacy", keyList: ["read", "read users", "update"] },
  { title: "Overview", keyList: ["overview_read", "overview_write", "overview_update"] },
  { title: "History", keyList: ["history_read", "history_write", "history_update"] },
  { title: "Team", keyList: ["team_read", "team_write", "team_update"] },
  { title: "Integration", keyList: ["integration_read", "integration_write", "integration_update"] },
];

const EXPENSE_GROUPS = [
  { title: "Expenses", keyList: ["read", "create", "update", "delete"] },
  {
    title: "Office Expenses",
    keyList: ["office_read", "office_create", "office_update", "office_delete"],
  },
];

/** Shorter labels for modules that would otherwise look cryptic in the permissions UI. */
const MODULE_DISPLAY_NAME = {
  WFH_REQUESTS: "WFH requests",
  PRAISE_MESSAGES: "Team praise (dashboard message)",
  PROJECT_DOCUMENTATION: "Project documentation",
  COMPANY_DETAILS: "Company details",
  CLIENT_MANAGEMENT: "Client management",
  INVOICE_MANAGEMENT: "Invoice management",
  SALARY_SLIP: "Salary slips",
  COMPANY_POLICY: "Company policy",
  ADMIN_ACCESS: "Admin access",
  FINANCE_MANAGEMENT: "Salary management",
  FINANCE_DOCUMENTS: "Finance upload documents",
  FINANCE_EXPENSES: "Expenses management",
  SOP_MANAGEMENT: "SOP management",
  OFFBOARDING_DOCUMENTS: "Offboarding documents",
  PROJECT_CREDENTIALS: "Project credentials",
  PERFORMANCE_CRITERIA: "Performance criteria",
};

const formatPermissionLabel = (module, permission) => {
  if (module.id === permissionType.INVOICE_MANAGEMENT) {
    const map = {
      read: "View invoices",
      create: "Create invoices",
      update: "Update invoices",
      delete: "Delete invoices",
      "send mail": "Send invoice email",
    };
    return map[permission] || permission;
  }
  if (module.id === permissionType.SALARY_SLIP) {
    const map = {
      read: "View all salary slips",
      "view own": "View own salary slip",
      download: "Download salary slip PDF",
      "send mail": "Send salary slip email",
    };
    return map[permission] || permission;
  }
  if (module.id === permissionType.DAILY_HOURS) {
    const map = {
      read: "View own daily work updates",
      create: "Submit daily work updates",
      "read users": "View team daily work updates",
      update: "Update daily work updates",
      "send mail": "Send daily work updates email",
    };
    return map[permission] || permission;
  }
  if (module.id === permissionType.WFH_REQUESTS) {
    const map = {
      read: "View own WFH requests",
      create: "Request WFH",
      update: "Edit own pending WFH",
      delete: "Cancel own pending WFH",
      "cancel approved": "Request cancellation of approved WFH",
      "assign employee": "Assign WFH to employee (HR)",
    };
    return map[permission] || permission;
  }
  if (module.id === permissionType.PROJECT_MANAGEMENT) {
    const map = {
      read: "View boards",
      create: "Create (legacy — grants all board actions)",
      update: "Update boards (edit settings, members, background)",
      delete: "Delete boards",
      "create board": "Create board",
      "create list": "Create list",
      "add card": "Add card",
      "edit card": "Edit & delete cards",
    };
    return map[permission] || permission;
  }
  if (module.id === permissionType.FINANCE_DOCUMENTS) {
    const map = {
      read: "View documents",
      create: "Upload documents",
      delete: "Delete documents",
    };
    return map[permission] || permission;
  }
  if (module.id === permissionType.FINANCE_EXPENSES) {
    const map = {
      read: "View",
      create: "Add",
      update: "Edit",
      delete: "Delete",
      office_read: "View",
      office_create: "Add",
      office_update: "Edit",
      office_delete: "Delete",
    };
    return map[permission] || permission;
  }
  if (module.id === permissionType.SOP_MANAGEMENT) {
    const map = {
      read: "View SOPs (employees see assigned only)",
      create: "Upload SOPs",
      update: "Update SOPs",
      delete: "Delete SOPs",
      "assign employee": "Assign SOPs to employees",
    };
    return map[permission] || permission;
  }
  if (module.id === permissionType.OFFBOARDING_DOCUMENTS) {
    const map = {
      read: "View offboarding documents (employees see assigned only)",
      create: "Upload offboarding templates",
      update: "Update offboarding templates",
      delete: "Delete offboarding templates",
      "assign employee": "Assign offboarding documents to employees",
      submit: "Submit filled offboarding document",
      "view submissions": "View employee submissions",
    };
    return map[permission] || permission;
  }
  if (module.id === permissionType.PROJECT_CREDENTIALS) {
    const map = {
      read: "View project credentials, instructions, and resources",
      create: "Create credentials, instructions, and resources",
      update: "Update project credentials, instructions, and resources",
      delete: "Delete project credentials, instructions, and resources",
      "manage personal": "Create and manage personal credentials",
      "share credential": "Share project credentials with specific employees",
      "read project files": "View and download project files",
      "read shared files": "View and download shared files",
      "upload project files": "Upload and update project files",
      "upload shared files": "Upload and update shared files",
      "delete project files": "Delete project files",
      "delete shared files": "Delete shared files",
    };
    return map[permission] || permission;
  }
  if (module.id === permissionType.PERFORMANCE_CRITERIA) {
    const map = {
      read: "View own late arrival records",
      "read users": "View all employees' late arrival records",
      "send mail": "Send late arrival notice emails",
    };
    return map[permission] || permission;
  }
  if (module.id === permissionType.ADMIN_ACCESS) {
    return permission === "admin access" ? "Admin access" : permission;
  }
  if (module.id === permissionType.PROJECT_DOCUMENTATION) {
    return formatProjectDocumentationPermissionLabel(permission);
  }
  if (module.id === permissionType.ATTENDANCE) {
    return formatAttendancePermissionLabel(permission);
  }
  return permission;
};

const getModuleDisplayName = (moduleName) =>
  MODULE_DISPLAY_NAME[moduleName] || moduleName;

const formatProjectDocumentationPermissionLabel = (permission) => {
  const map = {
    read: "View documentation",
    create: "Create documentation",
    "project filter": "Project filter (all projects)",
    "employee filter": "Employee filter (view by employee)",
    update: "Edit documents & approve edit requests",
    "request edit": "Request edit access (own documents)",
    "share document": "Share documents with employees",
  };
  return map[permission] || permission;
};

const AddRoleDrawer = ({ isOpen, toggleLeaveDrawer, role }) => {
  const { modules, moduleLoading: loading } = useSelector(
    (state) => state.role
  );
  const [roleName, setRoleName] = useState("");
  const [errors, setErrors] = useState({ roleName: "", permissions: "" });
  const dispatch = useDispatch();
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedRole, setSelectedRole] = useState(undefined);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const [originalPermissions, setOriginalPermissions] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [customEmails, setCustomEmails] = useState([]);
  const [customEmailsError, setCustomEmailsError] = useState("");
  const [customEmailInput, setCustomEmailInput] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    dispatch(getAllRoleModulesAPI());
  }, [isOpen, dispatch]);
  useEffect(() => {
    // Initialize form with role data if in edit mode
    if (role && role.roleId) {
      setIsEditMode(true);
      setRoleName(role.roleName);

      // Map role permissions to the format used by selectedModules
      const mappedPermissions = role.permissions.map((perm) => ({
        id: perm.moduleId,
        permissions: perm.moduleKey,
      }));

      setSelectedModules(mappedPermissions);
      setOriginalPermissions(JSON.parse(JSON.stringify(mappedPermissions))); // Deep copy for comparison
    } else {
      setIsEditMode(false);
      setRoleName("");
      setSelectedModules([]);
      setOriginalPermissions([]);
    }
  }, [role]);
  const handleModuleChange = (module, permission) => {
    // Create a new array to avoid direct state mutation
    const updatedModules = [...selectedModules];

    // Find the module in the array if it exists
    const moduleIndex = updatedModules.findIndex((mod) => mod.id === module.id);
    if (moduleIndex !== -1) {
      // Module exists, update its permissions
      const existingModule = updatedModules[moduleIndex];
      // Create a new array for permissions to avoid mutation issues
      const permissions = [...(existingModule.permissions || [])];

      // Toggle the permission
      const permissionIndex = permissions.indexOf(permission);
      if (permissionIndex !== -1) {
        // Remove permission if it exists - use filter instead of splice
        const newPermissions = permissions.filter(
          (p, i) => i !== permissionIndex
        );

        // Update the module with new permissions
        updatedModules[moduleIndex] = {
          ...existingModule,
          permissions: newPermissions,
        };
      } else {
        // Add permission if it doesn't exist
        updatedModules[moduleIndex] = {
          ...existingModule,
          permissions: [...permissions, permission],
        };
      }
    } else {
      // Module doesn't exist, add it with the permission
      updatedModules.push({
        id: module.id,
        permissions: [permission],
      });
    }

    setSelectedModules(updatedModules);
  };
  const validateForm = () => {
    let isValid = true;
    const newErrors = { roleName: "", permissions: "" };

    // Validate role name
    if (!roleName.trim()) {
      newErrors.roleName = "Role name is required";
      isValid = false;
    }

    // Check if at least one permission is selected
    const hasPermissions = selectedModules.some(
      (module) => module.permissions && module.permissions.length > 0
    );

    if (!hasPermissions) {
      newErrors.permissions = "At least one permission must be selected";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };
  const handleSubmit = () => {
    if (validateForm()) {
      if (isEditMode) {
        // Create update payload
        const payload = {
          roleName: roleName,
        };

        // Calculate added, updated, and removed permissions
        let addedModules = [];
        let updatedModules = [];
        let removedModuleIds = [];

        // Find added and updated modules
        selectedModules.forEach((module) => {
          // Create a map of existing permissions for faster lookup
          const existingPermissionMap = new Map();

          // Find the corresponding module in role permissions
          const roleModule = role.permissions.find(
            (perm) => perm.moduleId === module.id
          );

          if (!roleModule) {
            // This is a new module with permissions to add
            if (module.permissions && module.permissions.length > 0) {
              addedModules.push({
                moduleId: module.id,
                value: module.permissions,
              });
            }
          } else {
            // The module exists, check for permission changes

            // Create a Set from the existing permissions for O(1) lookups
            const existingPermissions = new Set(roleModule.moduleKey);
            // Create a Set from the new permissions
            const newPermissions = new Set(module.permissions);

            // Check if permissions have changed
            let hasChanged = existingPermissions.size !== newPermissions.size;

            if (!hasChanged) {
              // Check if any permissions are different
              for (const perm of newPermissions) {
                if (!existingPermissions.has(perm)) {
                  hasChanged = true;
                  break;
                }
              }
            }

            if (hasChanged) {
              if (newPermissions.size > 0) {
                updatedModules.push({
                  moduleId: module.id,
                  value: Array.from(newPermissions),
                });
              } else {
                removedModuleIds.push(module.id);
              }
            }
          }
        });

        // Check for modules that have been completely removed
        role.permissions.forEach((perm) => {
          const stillExists = selectedModules.some(
            (module) => module.id === perm.moduleId
          );
          if (!stillExists) {
            removedModuleIds.push(perm.moduleId);
          }
        });

        // Add modification data to payload if they exist
        if (addedModules.length > 0) {
          payload.addPermissions = addedModules;
        }

        if (updatedModules.length > 0) {
          payload.updatePermissions = updatedModules;
        }

        if (removedModuleIds.length > 0) {
          payload.removePermissions = removedModuleIds;
        }

        dispatch(editRoleAPI({ roleId: role.roleId, data: payload }));

        // Uncomment and replace with your update API call
        // dispatch(updateRoleAPI(payload));
      } else {
        // Create new role payload
        let data = {
          roleName: roleName,
          permissions: selectedModules.map((module) => ({
            moduleId: module.id,
            value: module.permissions,
          })),
        };
        dispatch(createRoleAPI(data));
      }

      toggleLeaveDrawer();
    }
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    dispatch(deleteRoleAPI({ roleId: role.roleId }));
    setIsDeleteModalOpen(false);
    toggleLeaveDrawer();
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  return (
    <div>
      <Drawer
        open={isOpen}
        onClose={toggleLeaveDrawer}
        placement={window.innerWidth > 700 ? "right" : "bottom"}
        className="drawer"
        title={isEditMode ? "Update Role" : "Add Role"}
        width="min(920px, 100vw)"
      >
        {loading ? (
          <Loader />
        ) : (
          <div className="flex flex-col gap-4">
            <div>
              <Input
                label="Role Name"
                value={roleName}
                onChange={(e) => {
                  setRoleName(e.target.value);
                  // Clear error when user types
                  if (e.target.value.trim()) {
                    setErrors({ ...errors, roleName: "" });
                  }
                }}
              />
              {errors.roleName && (
                <div className="text-red-500 text-sm mt-1">
                  {errors.roleName}
                </div>
              )}
              <div className="flex flex-col gap-4 mt-6">
                {errors.permissions && (
                  <div className="text-red-500 text-sm mb-2">
                    {errors.permissions}
                  </div>
                )}
                {modules?.map((module) => {
                  const keys = normalizeModuleKeys(module.keys);
                  if (module.id === permissionType.ATTENDANCE) {
                    return (
                      <div key={module.id} className="mt-2 flex flex-col gap-3 border-b border-[#e6ebf6] pb-4 last:border-0">
                        <div className="font-semibold text-[#27314a]">{getModuleDisplayName(module.moduleName)}</div>
                        {ATTENDANCE_GROUPS.map((group) => {
                          const groupKeys = group.keyList.filter((k) => keys.includes(k));
                          if (groupKeys.length === 0) return null;
                          return (
                            <div key={group.title} className="pl-0 md:pl-2">
                              <div className="text-xs font-medium text-[#6f7d97] mb-2">{group.title}</div>
                              <div className="flex flex-wrap gap-x-4 gap-y-2">
                                {groupKeys.map((permission) => (
                                  <Checkbox
                                    key={permission}
                                    onChange={() => handleModuleChange(module, permission)}
                                    checked={selectedModules.some(
                                      (mod) =>
                                        mod.id === module.id &&
                                        mod.permissions &&
                                        mod.permissions.includes(permission)
                                    )}
                                  >
                                    {formatAttendancePermissionLabel(permission)}
                                  </Checkbox>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                  if (module.id === permissionType.FINANCE_EXPENSES) {
                    return (
                      <div key={module.id} className="mt-2 flex flex-col gap-3 border-b border-[#e6ebf6] pb-4 last:border-0">
                        <div className="font-semibold text-[#27314a]">{getModuleDisplayName(module.moduleName)}</div>
                        {EXPENSE_GROUPS.map((group) => {
                          const groupKeys = group.keyList.filter((k) => keys.includes(k));
                          if (groupKeys.length === 0) return null;
                          return (
                            <div key={group.title} className="pl-0 md:pl-2">
                              <div className="text-xs font-medium text-[#6f7d97] mb-2">{group.title}</div>
                              <div className="flex flex-wrap gap-x-4 gap-y-2">
                                {groupKeys.map((permission) => (
                                  <Checkbox
                                    key={permission}
                                    onChange={() => handleModuleChange(module, permission)}
                                    checked={selectedModules.some(
                                      (mod) =>
                                        mod.id === module.id &&
                                        mod.permissions &&
                                        mod.permissions.includes(permission)
                                    )}
                                  >
                                    {formatPermissionLabel(module, permission)}
                                  </Checkbox>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                  return (
                    <div key={module.id} className="mt-2 flex flex-col sm:flex-row sm:gap-4 gap-2">
                      <span className="font-medium text-[#27314a] shrink-0">{getModuleDisplayName(module.moduleName)}</span>
                      <div className="flex flex-wrap gap-2">
                        {keys.map((permission) => (
                          <Checkbox
                            onChange={() => handleModuleChange(module, permission)}
                            key={permission}
                            checked={selectedModules.some(
                              (mod) =>
                                mod.id === module.id &&
                                mod.permissions &&
                                mod.permissions.includes(permission)
                            )}
                          >
                            {formatPermissionLabel(module, permission)}
                          </Checkbox>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* <div>
                            <h3 className="text-lg font-medium mb-3">Select Leave Approver</h3>
                            <RoleSelection onChangeRole={(val)=>{
                                setSelectedRole(val?.value);
                            }} selectedRole={selectedRole} />
                            <EmployeeSelect className='!w-full min-h-[40px]' disabled={!selectedRole} width='full' role={selectedRole} isMultiple={true} onSelectEmployee={(data)=>{
                                setSelectedUsers(data)
                            }}/>
                            
                            <div className='mt-4'>
                                <p className='text-sm text-gray-500'> Add Custom Emails </p>
                                {customEmails.length > 0 && 
                                <div className='flex gap-2 flex-wrap my-4'>
                                    {customEmails.map((email,index)=>(
                                        <div key={index} className=' flex w-fit gap-2 items-center bg-gray-100 rounded-[.375rem] h-[40px] px-4 py-1 text-sm cursor-pointer'>
                                            <p>{email}</p>
                                            <div className='text-black flex items-center h-[15px]  text-sm cursor-pointer ' onClick={()=>{
                                                setCustomEmails(customEmails.filter((_,i)=>i !== index))
                                            }} >X</div>
                                        </div>
                                    ))}
                                </div>
                                }
                                <div className='flex gap-2 items-end'>
                                    <Input placeholder='Enter Email' onChange={(e)=>{
                                        setCustomEmailsError("");
                                        setCustomEmailInput(e.target.value)
                                    }} value={customEmailInput} />

                                    <div className={"button bg-btn-primary hover:bg-primary text-white flex items-center rounded-[.375rem] h-[44px] px-4 py-1 text-sm cursor-pointer"} onClick={()=>{
                                       if(customEmailInput.trim() === "" || !validateEmail(customEmailInput)){
                                            setCustomEmailsError("Valid email is required")
                                        }else{
                                            setCustomEmails([...customEmails,customEmailInput])
                                            setCustomEmailInput("")
                                        }
                                    }} >Add</div>
                                </div>
                                {customEmailsError && <div className="text-red-500 text-sm mt-1">{customEmailsError}</div>}
                            </div>
                          
                        </div> */}
            <div className="flex justify-end gap-4">
              {isEditMode && (
                <AddButton
                  buttonTitle="Delete"
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700"
                />
              )}
              <AddButton
                buttonTitle={isEditMode ? "Update" : "Add"}
                onClick={handleSubmit}
              />
            </div>
          </div>
        )}
      </Drawer>

      <Modal
        title="Delete Role"
        open={isDeleteModalOpen}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText="Yes, Delete"
        cancelText="Cancel"
        okButtonProps={{
          danger: true,
        }}
      >
        <p>Are you sure you want to delete the role "{roleName}"?</p>
        <p>This action cannot be undone.</p>
      </Modal>
    </div>
  );
};

export default AddRoleDrawer;
